import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react';
import { useSelector } from 'react-redux';

import actions from '../../../actions';
import {
    Text,
    View
} from '../../../common';
import { ConsentCheckbox } from '../../../common/ConsentCheckbox';
import {
    CHILD_CLINIC_HISTORY_KEY,
    CHILD_HISTORY_KEY,
    isClinicHaveCapability,
    isConsentProvided,
    ONLINE_BOOKING_CHILD_CLINIC,
    ONLINE_BOOKING_CLINIC,
    usePersonalData,
    USER_CLINIC_HISTORY_KEY,
    USER_HISTORY_KEY
} from '../../../hooks/usePersonalData';
import {
    getProviderCapabilities,
    getProviderCodesClinics
} from '../../../selectors/providerCodes';
import { eventBus } from '../../../services/eventBus';

const loadProviderCapabilities = (providers) => {
    actions.loadProviderCapabilities({
        providers,
        onComplete: () => {
            eventBus.emit('checkUserConsents', {
                currentConsent: null,
                from: 'visitConsentsLoadProviderCapabilities',
                error: null,
                context: this,
                codePosition: 'VisitConsents.js loadProviderCapabilities onComplete'
            });
        },
        onError: (error) => {
            eventBus.emit('checkUserConsents', {
                currentConsent: null,
                from: 'visitConsentsLoadProviderCapabilities',
                error,
                context: this,
                codePosition: 'VisitConsents.js loadProviderCapabilities onError',
            });

        },
    });
};

export const VisitConsents = ({
    profileId,
    branchId,
    onConsentsReceived,
    needAibolitConsent = true,
    isChild = false,
    disabled = false,
}) => {
    const {
        consents,
    } = usePersonalData();
    const clinics = useSelector(getProviderCodesClinics);
    const lastConsentsCalledParams = React.useRef(null);
    const [currentHistoryConsent, setCurrentHistoryConsent] = useState(false);
    const [currentClinicConsent, setCurrentClinicConsent] = useState(false);
    const [currentClinicHistoryConsent, setCurrentClinicHistoryConsent] = useState(false);
    const [loadedProvirerCode, setLoadedProvirerCode] = useState(false);
    const [isProviderCapabilitiesLoaded, setIsProviderCapabilitiesLoaded] = useState(false);
    const capabilities = useSelector(getProviderCapabilities);
    const providerCode = useMemo(() => clinics?.[branchId]?.providerCode, [clinics, branchId]);
    // Select consent keys based on isChild prop
    const historyKey = useMemo(() => isChild ? CHILD_HISTORY_KEY : USER_HISTORY_KEY, [isChild]);
    const clinicHistoryKey = useMemo(() => isChild ? CHILD_CLINIC_HISTORY_KEY : USER_CLINIC_HISTORY_KEY, [isChild]);
    const clinicConsentKey = useMemo(() => isChild ? ONLINE_BOOKING_CHILD_CLINIC : ONLINE_BOOKING_CLINIC, [isChild]);

    useEffect(() => {
        if (providerCode && capabilities?.[providerCode] === undefined && !isProviderCapabilitiesLoaded) {
            loadProviderCapabilities([providerCode]);
            setIsProviderCapabilitiesLoaded(true);
        }

    }, [ providerCode, capabilities]);

    const haveCapability = useCallback((key) => {
        try {

            return isClinicHaveCapability({
                capabilities,
                providerCode,
                consent: key
            });
        } catch (e) {
            return false;
        }
    }, [capabilities, providerCode]);

    //region consents provided
    const isHistoryConsent = useMemo(() => isConsentProvided({
        profileId,
        consents,
        providerCode,
        agreementKey: historyKey,
    }) || !needAibolitConsent, [profileId,
        consents,
        providerCode,
        needAibolitConsent,
        historyKey]);

    const isClinicConsent = useMemo(() => haveCapability(clinicConsentKey) ? isConsentProvided({
        profileId,
        consents,
        providerCode,
        agreementKey: clinicConsentKey,
    }) : true, [profileId,
        consents,
        providerCode,
        clinicConsentKey,
        haveCapability]);

    const isClinicHistoryConsent = useMemo(() => haveCapability(clinicHistoryKey) ? isConsentProvided({
        profileId,
        consents,
        providerCode,
        agreementKey: clinicHistoryKey,
    }) : true, [profileId,
        consents,
        providerCode,
        clinicHistoryKey,
        haveCapability]);
    //endregion

    //region visible
    const isHistoryConsentVisible = useMemo(() => {
        return !isHistoryConsent;
    }, [isHistoryConsent]);

    const isClinicConsentVisible = useMemo(() => {
        return !isClinicConsent;
    }, [isClinicConsent]);

    const isClinicHistoryConsentVisible = useMemo(() => {
        return !isClinicHistoryConsent;
    }, [isClinicHistoryConsent]);

    //endregion

    // Handle disabled state
    useEffect(() => {
        if (disabled) {
            onConsentsReceived?.(true, {});
        }
    }, [disabled,
        onConsentsReceived,
        providerCode]);

    useEffect(() => {
        if (disabled) {
            return;
        }
        const param = !!(currentClinicConsent || isClinicConsent);
        //&& !!(currentHistoryConsent || isHistoryConsent) && !!(currentClinicHistoryConsent || isClinicHistoryConsent);
        const consents = {};

        // Add clinic history consent if visible
        if (isClinicHistoryConsentVisible) {
            consents[clinicHistoryKey] = currentClinicHistoryConsent;
        }

        // Add clinic consent if visible
        if (isClinicConsentVisible) {
            consents[clinicConsentKey] = currentClinicConsent;
        }

        // Add history consent if visible
        if (isHistoryConsentVisible) {
            consents[historyKey] = currentHistoryConsent;
        }

        const out = {
            consents,
            providerCode,
        };
        if (JSON.stringify(param) !== JSON.stringify(lastConsentsCalledParams.current)) {
            lastConsentsCalledParams.current = {
                param,
                consents: out,
            };
            onConsentsReceived?.(param, out);
        }
    }, [currentClinicConsent,
        currentHistoryConsent,
        currentClinicHistoryConsent,
        isClinicHistoryConsent,
        isClinicConsent,
        isHistoryConsent,
        isClinicHistoryConsentVisible,
        isClinicConsentVisible,
        isHistoryConsentVisible,
        lastConsentsCalledParams,
        onConsentsReceived,
        providerCode,
        clinicHistoryKey,
        clinicConsentKey,
        disabled,
        historyKey]);
    useEffect(() => {
        // Should be applied when no visits existed before and the first visit is created
        if (!loadedProvirerCode && !providerCode) {
            setLoadedProvirerCode(true);
            actions.loadProviderCodes({ branches: [branchId] });
        }
    }, [providerCode,
        loadedProvirerCode,
        branchId]);

    if (!branchId) {
        return <Text>No clinic data found</Text>;
    }

    if (disabled) {
        return null;
    }

    return <View style={[{ paddingHorizontal: 12, marginBottom: 12 }]}>
        {isClinicConsentVisible && <ConsentCheckbox
            profileId={profileId}
            providerCode={providerCode}
            onChange={setCurrentClinicConsent}
            agreementKey={clinicConsentKey}
            containerStyle={{ marginTop: 12 }}
        />}
        {isClinicHistoryConsentVisible && <ConsentCheckbox
            profileId={profileId}
            providerCode={providerCode}
            onChange={setCurrentClinicHistoryConsent}
            agreementKey={clinicHistoryKey}
            containerStyle={{ marginTop: 12 }}
        />}
        {isHistoryConsentVisible && <ConsentCheckbox
            profileId={profileId}
            providerCode={providerCode}
            onChange={setCurrentHistoryConsent}
            agreementKey={historyKey}
            containerStyle={{ marginTop: 12 }}
        />}
    </View>;
};
